import { HttpClient, HttpParams } from '@angular/common/http';
import { Directive } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, lastValueFrom, map, of, timeout } from 'rxjs';
import * as uuid from 'uuid';
import { SnackbarComponent } from '../components';

@Directive({})
export class UniversalConnectorVersionTwoDirective {
  public url: string = '';

  public crm: string = '';

  public dateRange: string = '';

  public patronId: string = '';

  constructor(
    connectorUrl: string,
    crm: string,
    private http: HttpClient,
    private readonly snackBar: MatSnackBar
  ) {
    this.url = connectorUrl;
    if (!crm) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        panelClass: 'error',
        duration: 5000,
        horizontalPosition: 'end',
        data: {
          type: 'error',
          message: 'crm name is requiered',
        },
      });
    }
    this.crm = crm.toLocaleLowerCase();
  }

  public getPatronProfile(patronId: string) {
    return lastValueFrom(
      this.http.get(`${this.url}/api/Player/get/${this.crm}/${patronId}`)
    );
  }

  public getPlayerBalances(patronId: string): Promise<any> {
    return lastValueFrom(
      this.http.get(`${this.url}/api/Balance/get/${this.crm}/${patronId}`)
    );
  }

  public getPlayerBalancesWinLossYears(patronId: string): Promise<any> {
    return lastValueFrom(
      this.http.get(
        `${this.url}/api/Balance/winlossyears/${this.crm}/${patronId}`
      )
    );
  }

  public getPlayerBalancesWinLoss(
    patronId: string,
    years: string,
    deliveryMethod: string
  ): Promise<any> {
    const queryParams = new URLSearchParams({
      years: years.toString(),
      deliveryMethod: deliveryMethod,
    }).toString();

    return lastValueFrom(
      this.http.get(
        `${this.url}/api/Balance/winloss/${this.crm}/${patronId}?${queryParams}`
      )
    );
  }

  public getPlayerBalancesWinLoss1(
    patronId: string,
    years: string,
    deliveryMethod: string
  ): Observable<any> {
    const url = `${this.url}/api/Balance/winloss/${this.crm}`;

    let params = new HttpParams();
    params = params.append('patronId', patronId);
    params = params.append('years', years);
    params = params.append('deliveryMethod', deliveryMethod);

    return this.http.get<any>(url, { params });
  }

  public updatePlayerProfile(patronId: string, type: string, body: any) {
    return lastValueFrom(
      this.http.put(
        `${this.url}/api/Player/${type}/put/${this.crm}/${patronId}`,
        body
      )
    );
  }

  public getPlayerActivity(patronId: string, dateRange?: string, campaign?: any, stringProperty?: string) {
    const d = this.getDateRange(
      dateRange ? dateRange : this.dateRange, campaign ?? null, stringProperty as string
    );
    return lastValueFrom(
      this.http.get(
        `${this.url}/api/Activity/get/${
          this.crm
        }/${patronId}${d}`
      )
    );
  }

  private getEarnStartDate(properties: any, stringProperty?: string) {
    return properties[`${stringProperty}EarnStartDate`] ?? properties.earnPeriodStartDate;
  }

  private getEarnEndDate(properties: any, stringProperty?: string) {
    return properties[`${stringProperty}EarnEndDate`] ?? properties.earnPeriodEndDate;
  }

  private getDateRange(dateRange: string, campaign?: any, stringProperty?: string) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const sevenDays = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysYear = sevenDays.getFullYear();
    const sevenDaysMonth = String(sevenDays.getMonth() + 1).padStart(2, '0');
    const sevenDaysDay = String(sevenDays.getDate()).padStart(2, '0');

    const getLastMonthStart = new Date(new Date(today.setDate(1)).setMonth(today.getMonth() - 1));
    const getLastMonthEnd = new Date(new Date(today.setDate(1)).setHours(-1));
    const lastMonthYear = getLastMonthStart.getFullYear();
    const lastMonth = String(getLastMonthStart.getMonth() + 1).padStart(2, '0');
    const lastMonthEndDay = String(getLastMonthEnd.getDate()).padStart(2, '0');

    let earnStartDate = '';
    let earnEndDate = '';

    if (dateRange === 'earnPeriod' && campaign && campaign.customProperties && campaign.customProperties?.showEarnPeriod) {
      earnStartDate = this.getEarnStartDate(campaign.customProperties, stringProperty as string);
      earnEndDate = this.getEarnEndDate(campaign.customProperties, stringProperty as string);
    }

    switch (dateRange) {
      case 'perDay':
        return `?from=${year}-${month}-${day}&to=${year}-${month}-${day}`;
      case 'sevenDays':
        return `?from=${sevenDaysYear}-${sevenDaysMonth}-${sevenDaysDay}&to=${year}-${month}-${day}`;
      case 'currentMonth':
        return `?from=${year}-${month}-01&to=${year}-${month}-${day}`;
      case 'lastMonth':
        return `?from=${lastMonthYear}-${lastMonth}-01&to=${lastMonthYear}-${lastMonth}-${lastMonthEndDay}`;
      case 'earnPeriod':
        return `?from=${earnStartDate}&to=${earnEndDate}`;
      default:
        return `?from=${year}-${month}-${day}&to=${year}-${month}-${day}`;
    }
  }

  public doBalanceAdjustment(
    patronId: string,
    points: string | number,
    balance: any,
    type: 'D' | 'C',
    balanceToUpdate?: any,
    description?: string,
    dailyLimit?: string,
    dollarAmount?: string,
    accountId?: string
  ) {
    const body = {
      TransactionId: '',
      Id: uuid.v4(),
      TransactionType: type === 'C' ? 'Deposit' : 'Withdraw',
      AccountId: balanceToUpdate?.id,
      AccountType: balanceToUpdate?.type,
      Amount: points.toString(),
      LocationType: 'POS',
      LocationId: 'MCD',
      Pin: '1234',
      DailyLimit: dailyLimit,
      DollarAmount: dollarAmount,
      AuthAccount: accountId,
      Description: description
    };

    return lastValueFrom(
      this.http
        .post(
          `${this.url}/api/Balance/transfer/post/${this.crm}/${patronId}`,
          body
        )
        .pipe(
          // timeout after 30s so UC hang doesn't silently succeed
          timeout(30000),
          map((res: any) => {
            return {
              result: res?.message,
              errorDetails: res?.errors?.[0]?.message,
            };
          })
        )
    );
  }

  public getMultipliers(): Observable<any> {
    return this.http.get(`${this.url}/api/Coupon/multiplier/${this.crm}/${this.patronId}`);
  }

  public claimMultiplier(
    id: string,
    earnStart: string,
    earnEnd: string,
    claimStart: string,
    claimEnd: string,
    rate: string,
    rewardSource: string,
    accountType: string
  ): Observable<any> {
    return this.http.post(
      `${this.url}/api/Coupon/multiplier/${this.crm}/${this.patronId}`,
      {
        id,
        earnStart,
        earnEnd,
        claimStart,
        claimEnd,
        rate,
        rewardSource,
        accountType
      }
    );
  }

  public getCmsOffersList() {
    return this.http
      .get(`${this.url}/api/Coupon/get/${this.crm}/${this.patronId}`)
      .pipe(map(({ coupons }: any) => coupons));
  }

  public getCmsDrawingList() {
    return this.http
      .get(
        `${this.url}/api/Balance/drawing-account/${this.crm}/${this.patronId}`
      )
      .pipe(map(({ drawings }: any) => drawings));
  }


  public getJoingoDrawingList() {
    return this.http
      .get(
        `${this.url}/api/Player/drawing/${this.crm}/${this.patronId}`
      )
      .pipe(map(({ drawings }: any) => drawings));
  }


  public getJoinGoDrawingActivate(
    patronId: string,
    drawingId: string
  ): Promise<any> {
    const queryParams = new URLSearchParams({
      drawingId: drawingId
    }).toString();

    return lastValueFrom(
      this.http.get(
        `${this.url}/api/Player/drawing/activate/${this.crm}/${patronId}?${queryParams}`
      )
    );
  }


  public redeemOffers(offer: any) {
    return this.http.post(
      `${this.url}/api/Coupon/transfer/post/${this.crm}/${this.patronId}`,
      offer
    );
  }

  public updateOffers(offer: any) {
    return this.http.put(
      `${this.url}/api/Coupon/put/${this.crm}/${this.patronId}`,
      offer
    );
  }
}
